import { useQuery } from '@tanstack/react-query';
import {
  AppWindow,
  Expand,
  ExternalLink,
  LayoutPanelTop,
  MessageCircle,
  Monitor,
  PanelLeftOpen,
  Smartphone,
} from 'lucide-react';
import { type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import type { Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { PreviewContext } from '@/Domain/components/PreviewProvider';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { Tabs, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useCollapsibleLabel } from '@/Domain/hooks/App/useCollapsibleLabel.hook';
import { cn } from '@/Domain/utils/cn';
import { BrowserIconButton } from '@/UI/ChatPage/SafariBrowserFrame';
import { SIDE_PANEL_COLLAPSED_SIZE } from '@/UI/Website/PreviewSidePanel';
import { WidgetPreview } from '@/UI/Website/WidgetPreview';
import { usePreviewWebsite } from '@/UI/Website/usePreviewWebsite';
import { WEBSITE_VIEW_QUERY_PARAM, WebsiteView, isValidWebsiteView } from '@/UI/Website/viewMode';
import { CHAT_PAGE_ACCESS_TOKEN_KEY, getPreviewIdentityToken } from '@/context';

export type DeviceMode = 'mobile' | 'desktop';
type ColorSchemeMode = 'light' | 'dark';

const FAKE_URL = 'https://yourwebsite.com';

// Fixed-size mockup: overflows while the left panel is open, fills the right panel once collapsed.
const LEFT_PANEL_COLLAPSED_FRACTION = SIDE_PANEL_COLLAPSED_SIZE / 100;
// pr-5 (20) + 20px left gap; inset both sides so the mockup never touches the edge.
const PANEL_HORIZONTAL_PADDING = 40;
// Desktop chrome (address bar) height, excluded when height-fitting. Mobile's bar overlays the screenshot, so it adds no height.
const BROWSER_CHROME_HEIGHT = 56;
// Top/bottom breathing room so the mockup's borders aren't clipped when height-constrained.
const MOCKUP_VERTICAL_MARGIN = 12;
const MOCKUP_CONTENT_ASPECT = 16 / 9;
// Cap at the screenshot's native 1280px so it's never upscaled.
const MOCKUP_MAX_CONTENT_WIDTH = 1280;
// Fixed 390:844 phone viewport so the full mobile page shows; capped at native 390.
const MOBILE_CONTENT_ASPECT = 390 / 844;
const MOBILE_MAX_CONTENT_WIDTH = 390;

type WidgetPreviewPanelProps = {
  isFocused?: boolean;
  isLeftPanelCollapsed?: boolean;
  onToggleFocus?: () => void;
  // Reported, not acted on: each page decides whether the device mode should drive the side panel.
  onDeviceModeChange?: (mode: DeviceMode) => void;
  advancedSettings?: ReactNode;
  language?: string;
  // Forwarded to NewWidget; the kept-alive AI Preview host passes false (see NewWidgetProps.bindContextIframe).
  bindContextIframe?: boolean;
  // Forwarded to NewWidget; the kept-alive AI Preview host passes its own isActive.
  detectSupportIntent?: boolean;
};

export const WidgetPreviewPanel = ({
  isFocused,
  isLeftPanelCollapsed,
  onToggleFocus,
  onDeviceModeChange,
  advancedSettings,
  language,
  bindContextIframe,
  detectSupportIntent,
}: WidgetPreviewPanelProps) => {
  const { t } = useTranslation(['website', 'common']);
  const [searchParams] = useSearchParams();
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('mobile');
  const initialView = searchParams.get(WEBSITE_VIEW_QUERY_PARAM);
  const [viewMode, setViewMode] = useState<WebsiteView>(
    isValidWebsiteView(initialView) ? initialView : WebsiteView.Bubble,
  );

  const handleDeviceModeChange = (mode: DeviceMode) => {
    setDeviceMode(mode);
    onDeviceModeChange?.(mode);
  };

  const integrationsData = useQuery<Integrations>({ queryKey: ['integrations'] });

  const widgetSettings = useQuery<WidgetSettingsDto>({ queryKey: ['widget-settings'] });
  const widgetThemeMode = widgetSettings.data?.theme ?? 'light';
  const effectiveColorScheme: ColorSchemeMode = widgetThemeMode === 'dark' ? 'dark' : 'light';

  const previewWebsite = usePreviewWebsite();

  const publicChatPageUrl = window.location.origin + (integrationsData?.data?.links.public ?? '');

  const handleOpenPreview = () => {
    let link: string | undefined;
    if (viewMode === WebsiteView.Bubble) link = integrationsData?.data?.links.widget;
    else if (viewMode === WebsiteView.Embed) link = integrationsData?.data?.links.embed;
    else link = integrationsData?.data?.links.public;
    if (link) window.open(window.location.origin + link, '_blank', 'noopener,noreferrer');
  };

  const previewContext = useContext(PreviewContext);
  // The unsaved choice the preview already shows, else the saved one (pages without the Appearance form).
  const widgetTransparent =
    (previewContext?.previewTransparencyStyle ??
      widgetSettings.data?.widget_theme_custom?.transparency?.style) === 'glass';
  const prevBubbleReqRef = useRef(0);
  useEffect(() => {
    const reqId = previewContext?.bubbleViewRequestId ?? 0;
    if (reqId > prevBubbleReqRef.current) {
      prevBubbleReqRef.current = reqId;
      setViewMode(WebsiteView.Bubble);
    }
  }, [previewContext?.bubbleViewRequestId]);

  // Show tab tooltips only once labels collapse at the @2xl container query (hook tracks the sentinel).
  const {
    observeRef: previewSectionRef,
    sentinelRef: tabLabelSentinelRef,
    isHidden: labelsHidden,
  } = useCollapsibleLabel<HTMLDivElement, HTMLSpanElement>();
  const showLabels = !labelsHidden;

  const previewAreaRef = useRef<HTMLDivElement>(null);
  const [mockupWidth, setMockupWidth] = useState<number>();
  const [reloadToken, setReloadToken] = useState(0);
  // Refresh and address edits are both browser navigations, so the widget must boot fresh instead of carrying session-only state like a dismissed launcher into a new page.
  const startNewPageView = () => setReloadToken((prev) => prev + 1);
  useEffect(() => {
    const area = previewAreaRef.current;
    if (!area) return;
    const isMobileMode = deviceMode === 'mobile';
    const aspect = isMobileMode ? MOBILE_CONTENT_ASPECT : MOCKUP_CONTENT_ASPECT;
    const maxWidth = isMobileMode ? MOBILE_MAX_CONTENT_WIDTH : MOCKUP_MAX_CONTENT_WIDTH;
    const chromeHeight = isMobileMode ? 0 : BROWSER_CHROME_HEIGHT;
    const group = area.closest('[data-panel-group]') as HTMLElement | null;
    const measure = () => {
      const groupWidth = group?.clientWidth ?? area.clientWidth;
      const fullWidth = groupWidth * (1 - LEFT_PANEL_COLLAPSED_FRACTION) - PANEL_HORIZONTAL_PADDING;
      const heightWidth =
        Math.max(0, area.clientHeight - chromeHeight - MOCKUP_VERTICAL_MARGIN) * aspect;
      setMockupWidth(Math.max(0, Math.min(fullWidth, heightWidth, maxWidth)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(area);
    if (group) ro.observe(group);
    return () => ro.disconnect();
  }, [deviceMode]);

  const previewIdentityToken = getPreviewIdentityToken();
  // A fresh object literal here is a changed dep for NewWidget's src memo on every render.
  const customParams = useMemo(
    () =>
      previewIdentityToken ? { [CHAT_PAGE_ACCESS_TOKEN_KEY]: previewIdentityToken } : undefined,
    [previewIdentityToken],
  );

  const isChatPageView = viewMode === WebsiteView.ChatPage;
  const newTabButton = (
    <BrowserIconButton
      icon={ExternalLink}
      label={t('preview_link.tooltip')}
      onClick={handleOpenPreview}
      data-testid="open-preview-button"
      tooltip={
        <>
          <p>{t('preview_link.tooltip')}</p>
          {isChatPageView && (
            <p className="text-muted-foreground">{t('preview_link.access_info')}</p>
          )}
        </>
      }
    />
  );
  const isBubbleView = viewMode === WebsiteView.Bubble;
  // Empty when unknown/cleared: address bar shows the placeholder, background falls back to the skeleton.
  const hasPreviewUrl = !!previewWebsite.data?.url;
  const previewUrl = isChatPageView
    ? publicChatPageUrl
    : isBubbleView
      ? (previewWebsite.data?.url ?? '')
      : FAKE_URL;

  return (
    <div
      ref={previewSectionRef}
      className="@container relative flex h-full w-full flex-col overflow-hidden py-5 pr-5"
      // Dot-grid canvas backdrop via theme tokens so it tracks light/dark.
      style={{
        background:
          'radial-gradient(circle at 1px 1px, color-mix(in oklab, var(--muted-foreground) 32%, transparent) 1px, transparent 0) 0 0 / 20px 20px, ' +
          'var(--muted)',
      }}
    >
      {/* Edge shadow at the left-panel seam: hints the mockup continues behind it (resize / collapse to reveal). */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-12 bg-gradient-to-r from-white/55 to-transparent dark:from-black/30" />
      <TooltipProvider>
        <div className="flex items-center gap-2 shrink-0 mb-4 ml-5">
          {onToggleFocus && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onToggleFocus}
                  aria-label={isFocused ? t('common:show_panels') : t('common:hide_panels')}
                  className="size-7 shrink-0 hover:!text-foreground"
                  data-testid="preview-focus-toggle"
                >
                  {isFocused ? <PanelLeftOpen /> : <Expand />}
                </IconButton>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isFocused ? t('common:show_panels') : t('common:hide_panels')}
              </TooltipContent>
            </Tooltip>
          )}

          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as WebsiteView)}>
            <TabsList className="border border-border">
              <TabsTrigger
                value={WebsiteView.Bubble}
                icon={MessageCircle}
                aria-label={t('preview_controls.widget_aria')}
                tooltip={showLabels ? undefined : t('preview_controls.widget')}
                className="h-7 @max-2xl:gap-0"
              >
                <span ref={tabLabelSentinelRef} className="hidden @2xl:inline">
                  {t('preview_controls.widget')}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value={WebsiteView.Embed}
                icon={AppWindow}
                aria-label={t('preview_controls.embed_aria')}
                tooltip={showLabels ? undefined : t('preview_controls.embed')}
                className="h-7 @max-2xl:gap-0"
              >
                <span className="hidden @2xl:inline">{t('preview_controls.embed')}</span>
              </TabsTrigger>
              <TabsTrigger
                value={WebsiteView.ChatPage}
                icon={LayoutPanelTop}
                aria-label={t('preview_controls.chat_page_aria')}
                tooltip={showLabels ? undefined : t('preview_controls.chat_page')}
                className="h-7 @max-2xl:gap-0"
              >
                <span className="hidden @2xl:inline">{t('preview_controls.chat_page')}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Tabs value={deviceMode} onValueChange={(v) => handleDeviceModeChange(v as DeviceMode)}>
            <TabsList className="border border-border">
              <TabsTrigger
                value="mobile"
                icon={Smartphone}
                aria-label={t('preview_controls.mobile_aria')}
                tooltip={showLabels ? undefined : t('preview_controls.mobile')}
                className="h-7 @max-2xl:gap-0"
              >
                <span className="hidden @2xl:inline">{t('preview_controls.mobile')}</span>
              </TabsTrigger>
              <TabsTrigger
                value="desktop"
                icon={Monitor}
                aria-label={t('preview_controls.desktop_aria')}
                tooltip={showLabels ? undefined : t('preview_controls.desktop')}
                className="h-7 @max-2xl:gap-0"
              >
                <span className="hidden @2xl:inline">{t('preview_controls.desktop')}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {advancedSettings}

          <div data-testid="preview-color-toggle-locked" />
        </div>
      </TooltipProvider>

      <div
        ref={previewAreaRef}
        className={cn(
          'flex-1 flex items-center overflow-hidden',
          deviceMode === 'mobile' || isLeftPanelCollapsed ? 'justify-center' : 'justify-end',
          // Centered when the left panel is closed; while it's open the mockup overflows left behind it.
          isLeftPanelCollapsed && 'pl-5',
        )}
      >
        <div className="shrink-0" style={{ width: mockupWidth }}>
          <WidgetPreview
            viewMode={viewMode}
            deviceMode={deviceMode}
            url={previewUrl}
            customParams={customParams}
            headerAction={newTabButton}
            colorScheme={effectiveColorScheme}
            language={language}
            bindContextIframe={bindContextIframe}
            detectSupportIntent={detectSupportIntent}
            widgetTransparent={widgetTransparent}
            screenshotUrl={
              isBubbleView && hasPreviewUrl
                ? (previewWebsite.data?.screenshot_url ?? undefined)
                : undefined
            }
            screenshotMobileUrl={
              isBubbleView && hasPreviewUrl
                ? (previewWebsite.data?.screenshot_mobile_url ?? undefined)
                : undefined
            }
            isScreenshotLoading={isBubbleView && hasPreviewUrl && previewWebsite.isCapturing}
            screenshotFailed={isBubbleView && hasPreviewUrl && previewWebsite.didFail}
            onUrlChange={
              isBubbleView
                ? (nextUrl) => {
                    startNewPageView();
                    previewWebsite.setUrl.mutate({ url: nextUrl });
                  }
                : undefined
            }
            onRefresh={
              isBubbleView
                ? () => {
                    startNewPageView();
                    previewWebsite.refresh.mutate();
                  }
                : undefined
            }
            reloadToken={reloadToken}
            isRefreshing={
              isBubbleView && (previewWebsite.refresh.isPending || previewWebsite.isCapturing)
            }
          />
        </div>
      </div>
    </div>
  );
};
